import { useField } from 'formik';
import React, { useEffect, useRef, useState } from 'react';

import styled from '@emotion/styled';

import { FieldErrorInfo, InputWrapper } from './FormField';

export interface ImageUploadFieldProps {
  className?: string;
  id?: string;
  name: string;
  defaultImgUrl?: string | null;
  setDefaultImgUrl: (...args: any) => void;
  title?: string;
  showSupportText?: boolean;
}

const ImageUploadField: React.FC<ImageUploadFieldProps> = ({
  className,
  defaultImgUrl,
  setDefaultImgUrl,
  showSupportText = true,
  title,
  ...props
}) => {
  void setDefaultImgUrl;

  const [field, meta, helpers] = useField(props);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!field.value) {
      setPreviewUrl(null);

      if (inputRef.current) {
        inputRef.current.value = '';
      }

      return;
    }

    const file = field.value as File;
    const nextPreviewUrl = URL.createObjectURL(file);
    setPreviewUrl(nextPreviewUrl);

    return () => {
      URL.revokeObjectURL(nextPreviewUrl);
    };
  }, [field.value]);

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files && event.target.files[0];
    helpers.setValue(file ?? null);
  };

  return (
    <InputWrapper className={className}>
      <InputContainer>
        <ButtonArea>
          <ImageUploaderLabel htmlFor={props.name || props.id}>
            <ImageContainer>
              <Img
                src={`${previewUrl ?? defaultImgUrl ?? '/static/img/profilePlaceholder.png'}`}
                alt='profile image'
              />
            </ImageContainer>
            <TextArea>
              <BoldText>{title ?? 'Upload image of driver'}</BoldText>
              {showSupportText && <LightText>Supports JPEG and PNG</LightText>}
            </TextArea>
            <ImgInputField
              ref={inputRef}
              name={props.name}
              id={props.name}
              type='file'
              accept='image/*'
              onChange={handleImageChange}
            />
          </ImageUploaderLabel>
        </ButtonArea>
      </InputContainer>
      {meta.touched && meta.error ? (
        <FieldErrorInfo>{meta.error.capitalize()}</FieldErrorInfo>
      ) : null}
    </InputWrapper>
  );
};

export default ImageUploadField;

export const InputContainer = styled.div`
  display: flex;
  gap: 10px;
  /* border: 2px solid red; */
  align-items: center;
`;

const ImageUploaderLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 16px;
  width: fit-content;
  cursor: pointer;
`;

const ImageContainer = styled.div`
  display: grid;
  place-content: center;
  max-width: 78px;
  max-height: 78px;
  /* border: 1px solid ${({ theme }) => theme.palette.borderColor}; */
  /* border-radius: 50px; */
  overflow: hidden;
`;

const Img = styled.img`
  object-fit: fill;
  object-position: center center;
  padding: 6px;
`;

const TextArea = styled.div`
  display: flex;
  gap: 16px;
  flex-direction: column;
  font-size: 14px;
  font-weight: 400;
  line-height: 19.6px;
  color: #71748c;
  width: fit-content;
`;

const BoldText = styled.div`
  font-weight: 600;
  font-size: 16px;
  line-height: 16px;
  color: #232222;
`;

const LightText = styled.div`
  font-weight: 400;
  font-size: 14px;
  line-height: 14px;
  color: ${({ theme }) => theme.palette.grayTextColor};
`;

const ImgInputField = styled.input`
  display: none;
`;

const ButtonArea = styled.div`
  display: flex;
  gap: 16px;
`;
